<template>
  <div ref="mediaFormWrapper" class="media-form w-full md:w-343px lg:w-379px rounded-16px pb-32px shadow-full-form">
    <div class="text-center py-24px rounded-t-16px" :style="{ backgroundColor: getBgColor }">
      <h2 class="text-24 font-bold text-white leading-dot92">{{ job.media_page_content.custom_headline_1 }}</h2>

      <p class="text-base font-bold mt-4px text-white leading-1-56">{{ job.media_page_content.custom_headline_2 }}</p>
    </div>
    <form action="" method="POST" ref="mediaForm" @submit.prevent="handleClick">
      <div class="mt-6 px-6">

        <!-- First Name Field-->
        <div class="vx-col relative w-full mt-4">
          <label class="block mb-1 text-xs text-onyx-light">First Name</label>
          <vs-input ref="first_name" v-validate="{ required: true, alpha_spaces: true, differentNames: last_name }" @keyup="triggerValidation('last_name')" name="first_name" v-model.trim="first_name" class="w-full h-42px" maxlength="40" autofocus></vs-input>
          <span class="text-danger text-10" v-show="errors.has('first_name')">{{ errors.first('first_name') }}</span>
        </div>

        <!-- Last Name Field-->
        <div class="vx-col relative w-full mt-4">
          <label class="block mb-1 text-xs text-onyx-light">Last Name</label>
          <vs-input ref="last_name" v-validate="{ required: true, alpha_spaces: true, differentNames: first_name }" @keyup="triggerValidation('first_name')" name="last_name" v-model.trim="last_name" class="w-full h-42px" maxlength="40" ></vs-input>
          <span class="text-danger text-10" v-show="errors.has('last_name')">{{ errors.first('last_name') }}</span>
        </div>

        <!-- Email Field-->
        <div class="vx-col relative w-full mt-4">
          <label class="block mb-1 text-xs text-onyx-light">Email</label>
          <vs-input  v-validate="'required|email'" name="email" v-model="email" class="w-full h-42px" maxlength="100" ></vs-input>
          <span class="text-danger text-10" v-show="errors.has('email')">{{ errors.first('email') }}</span>
        </div>

        <!-- Phone Number Field-->
        <div class="vx-col relative w-full mt-4">
          <label for="phone" class="block mb-1 text-xs text-onyx-light">Mobile Phone</label>
          <vs-input type="tel" v-validate="{ required: true, usphone: true }" @input="setDirty('phone')" @blur="validatePhoneNumberOnBlur" id="phone" name="phone" v-model="phone" class="w-full h-42px" maxlength="14" ></vs-input>
          <span class="text-danger text-10" v-show="errors.has('phone')">{{ errors.first('phone') }}</span>
          <span class="text-danger text-10" v-show="sessionErrors" v-for="sessionError in sessionErrors" :key="sessionError">{{sessionError.includes('phone') ? sessionError : ''}}</span>
        </div>

        <!-- Address Field-->
        <div class="vx-col relative w-full mt-4">
          <label for="full_address" class="block mb-1 text-xs text-onyx-light">Address</label>
          <place-auto-suggest
            @no_results_found="noAddressResultsFound"
            @full_address="autoFillAddressFields"
            input-classes="h-42px p-2 border-chrome-light rounded"
            name="full_address"
            id="full_address"
            v-validate="'required'"
            :value="address"
            data-vv-name="full_address"
          />
          <span class="text-danger text-10"  v-show="errors.has('full_address')">{{ errors.first('full_address') }}</span>
        </div>

        <!-- Apartment/Suite Field -->
        <div v-if="full_address" class="vx-col relative w-full mt-4">
          <label for="apartment" class="block mb-1 text-xs text-onyx-light">Apartment/Suite</label>
          <vs-input id="apartment" name="apartment" v-model.trim="apartment" class="w-full h-42px" maxlength="40" ></vs-input>
        </div>

        <!-- City Field -->
        <div v-if="full_address" class="vx-col relative w-full mt-4">
          <label for="city" class="block mb-1 text-xs text-onyx-light">City</label>
          <vs-input id="city" name="city" v-model.trim="city" v-validate="'required'" class="w-full h-42px" maxlength="40" ></vs-input>
          <span class="text-danger text-10" v-show="errors.has('city')">{{ errors.first('city') }}</span>
        </div>

        <!-- State Field -->
        <div v-if="full_address" class="vx-col relative w-full mt-4">
          <label for="state" class="block mb-1 text-xs text-onyx-light">State</label>
          <vs-input id="state" name="state" v-model.trim="state" v-validate="'required'" class="w-full h-42px" maxlength="40" ></vs-input>
          <span class="text-danger text-10" v-show="errors.has('state')">{{ errors.first('state') }}</span>
        </div>

        <!-- Zip Code Field -->
        <div v-if="address" class="vx-col relative w-full mt-4">
          <label for="zip_code" class="block mb-1 text-xs text-onyx-light">Zip</label>
          <vs-input id="zip_code" name="zip_code" v-model.trim="zip_code" v-validate="'required'" class="w-full h-42px" maxlength="40" ></vs-input>
          <span class="text-danger text-10" v-show="errors.has('zip_code')">{{ errors.first('zip_code') }}</span>
        </div>

        <!-- Store Location Select field -->
        <div class="vx-col relative w-full mt-4" v-if="!fetchingLocations && storeLocations.length > 1">
          <label class="block mb-1 text-xs text-onyx-light">Store Location</label>
          <v-select
            class="w-full h-42px store_location"
            id="store_location"
            name="store_location"
            :options="storeLocations"
            placeholder="Select the location that is best for you"
            v-model.trim="store_location.value"
            v-validate="'required'"
            size="30"
          >
            <template #option="{ address, city, state, cityDistance }">
              <div>
                <div>{{ address }}</div>
                <div>{{ city }}, {{ state }} - {{ cityDistance }}</div>
              </div>
            </template>
            <template #selected-option="{ address, city, state, cityDistance }">
              <div>
                <div>{{ address }}</div>
                <div>{{ city }}, {{ state }} - {{ cityDistance }}</div>
              </div>
            </template>
          </v-select>

          <input type="hidden" :value="store_location.value ? store_location.value.value : ''" name="store_location">
          <span class="text-danger text-10 mt-3 block" v-show="errors.has('store_location')">{{ errors.first('store_location') }}</span>
        </div>

        <div v-if="hiring_event_toggle">
          <div v-if="groupedSlots.length === 1" class="vx-col relative w-full mt-6">
            <label for="selected_time_slot_id" class="text-sm font-medium mb-12px">What time will you arrive on {{ groupedSlots[0].date }}? (All time slots are in {{ timezoneMapping }}) </label>
            <media-radio-button
              class="multi_options time_slots"
              v-model="selected_time_slot"
              id="selected_time_slot_id"
              :options="timeSlotOptions(groupedSlots[0].slots)"
              :color="color"
              name="selected_time_slot"
              v-validate="'required'"
              data-vv-validate-on="change" />
            <span class="text-danger text-10" v-show="errors.has('selected_time_slot')">{{ errors.first('selected_time_slot') }}</span>
          </div>

          <div v-else-if="groupedSlots.length > 1" class="vx-col relative w-full mt-6">
            <label for="selected_date" class="text-sm font-medium mb-12px">Which date will you attend?</label>

            <media-radio-button
              class="multi_options"
              v-model="selected_date"
              :options="dateOptions"
              :color="color"
              id="selected_date"
              name="selected_date"
              v-validate="'required'"
              @change.native="onDateChange"
              data-vv-validate-on="change" />
            <span class="text-danger text-10" v-show="errors.has('selected_date')">{{ errors.first('selected_date') }}</span>

            <div v-if="selected_date" class="vx-col relative w-full mt-6">
              <label for="selected_time_slot_id" class="text-sm font-medium mb-12px">What time will you arrive? (All time slots are in {{ timezoneMapping }})</label>
              <media-radio-button
                class="multi_options time_slots"
                v-model="selected_time_slot"
                id="selected_time_slot_id"
                :options="timeSlotOptionsData"
                :color="color"
                name="selected_time_slot"
                v-validate="'required'"
                data-vv-validate-on="change" />
              <span class="text-danger text-10" v-show="errors.has('selected_time_slot')">{{ errors.first('selected_time_slot') }}</span>
            </div>
          </div>

          <div v-if="multiplePositions" class="vx-col relative w-full mt-6">
            <label for="selected_positions" class="text-sm font-medium mb-12px">Which position(s) are you interested in?</label>

            <media-radio-button
              class="multi_options"
              v-model="selected_positions"
              :options="positions"
              :color="color"
              id="selected_positions"
              name="selected_positions"
              :multi-select="true"
              v-validate="'required'"
              data-vv-validate-on="change" />
            <span class="text-danger text-10" v-show="errors.has('selected_positions')">{{ errors.first('selected_positions') }}</span>
          </div>
        </div>

        <div v-for="field in formTypeInputFields" :key="field.name" class="vx-col relative w-full mt-6">
          <div v-if="field.name === 'experienceFields'">
            <!-- Experience Level Fields -->
            <media-radio-button
                class="multi_options"
                v-model="mediaFullForm.experience"
                id="experience"
                :options="experiences"
                :title="job.experience_label"
                :color="color"
                name="experience"
                v-validate="'required'"
                data-vv-validate-on="change"
                :disabled="isExperienceFieldDisabled" />
            <span class="text-danger text-10" v-show="errors.has('experience')">{{ errors.first('experience') }}</span>
          </div>
          <div v-else-if="field.show(job.form_type.fields ?? {})">
            <!-- Regular Field -->
            <media-radio-button
                :name="field.name"
                :class="field.options.length <= 2 ? 'two_options' : 'multi_options'"
                :value="mediaFullForm[field.name] || ''"
                @input="$set(mediaFullForm, field.name, $event)"
                :id="field.name"
                :options="field.options"
                :title="formQuestionLabels[field.name]"
                :color="color"
                v-validate="Object.keys(field.validation || {}).join(',')"
            />
            <span class="text-danger text-10"
                v-for="validationRule in Object.keys(field.validation || {})"
                :key="validationRule"
                v-show="errors.items.find(item => item.field === field.name && item.rule === validationRule)"
            >
              {{field.validation[validationRule]}}
            </span>
          </div>
        </div>

        <template v-if="(job.form_type.fields && job.form_type.fields.endorsements || '').toLowerCase() === 'yes'">
          <div class="vx-col relative w-full mt-6">

            <!-- Hack to get the endorsements set in form from two v-models -->
            <input type="hidden" name="endorsements" ref="hidden_endorsements">
            <endorsements
                :options="endorsementsOptions"
                v-model="mediaFullForm.endorsements"
                :color="color"
                :title="formQuestionLabels['endorsements']"
                name="endorsements" v-validate="'required'"
            />


            <span class="text-danger text-10" v-show="errors.has('endorsements')">{{ errors.first('endorsements') }}</span>
          </div>
        </template>

<!--        Insert dynamic categories here. -->
        <category-types-loader :flags="['SELECT_MEDIA_FORM_SINGLE']" with-values>
          <template #categories="{ categories }">
            <div
                v-for="category in categories"
                v-bind:key="category.key"
                class="vx-col relative w-full mt-6"
            >
              <media-radio-button
                  class="multi_options"
                  :id="`category-type-${category.key}`"
                  @input="setCategoryValue(category.key, $event)"
                  :value="categorySelection[category.key] || null"
                  :options="category.values.map(v => ({ label: v.name, value: v.id }))"
                  :title="category.attributes.media_form_question"
                  :color="color"
                  :name="category.key"
              />
            </div>
          </template>
        </category-types-loader>

        <!-- Additional Questions Fields -->
        <div class="mt-6" v-for="(question, i) in questions" :key="i">
          <!-- Question Text -->
          <div v-if="question['type'] === 'text'">
            <label :for="'question_'+question['id']" class="block text-sm font-medium mb-12px">{{ question['title'] }}</label>
            <vs-input type="text" v-validate="question['is_required'] && 'required'" :id="'question_'+question['id']" :name="'question_'+question['id']" v-model="customFields['question_'+question['id']]" class="w-full" />
          </div>
          <!-- Question Number -->
          <div v-else-if="question['type'] === 'number'">
            <label :for="'question_'+question['id']" class="block text-sm font-medium mb-12px">{{ question['title'] }}</label>
            <vs-input type="number" v-validate="question['is_required'] && 'required'" :id="'question_'+question['id']" :name="'question_'+question['id']" v-model="customFields['question_'+question['id']]" class="w-full" />
          </div>
          <!-- Question TextArea -->
          <div v-else-if="question['type'] === 'textarea'" >
            <label :for="'question_'+question['id']" class="block text-sm font-medium mb-12px">{{ question['title'] }}</label>
            <vs-textarea v-validate="question['is_required'] && 'required'" :id="'question_'+question['id']" :name="'question_'+question['id']" v-model="customFields['question_'+question['id']]" class="w-full" />
          </div>
          <!-- Question Single Select -->
          <div v-else-if="['single-select', 'multi-select'].includes(question['type'])">
            <media-radio-button
                class="multi_options"
                :id="'question_'+question['id']"
                :options="question['options']"
                :title="question['title']"
                :color="color"
                :name="'question_'+question['id']"
                :value="
                  question['type'] === 'single-select'
                    ? customFields['question_'+question['id']] || ''
                    : multiSelects['question_'+question['id']] || []"
                @input="
                  question['type'] === 'single-select'
                    ? storeSingleSelect($event, 'question_'+question['id'])
                    : storeMultiSelect($event, 'question_'+question['id'])"
                :multi-select="question['type'] === 'multi-select'"
                v-validate="question['is_required'] && 'required'"
            />

            <!--Hack to support v-select as regular html form submission-->
            <input type="hidden" :name="'question_'+question['id']" :value="customFields['question_'+question['id']]" />
          </div>
          <!-- Question Dropdown -->
          <div v-else-if="question['type'] === 'dropdown'">
            <label :for="'question_'+question['id']" class="block text-sm font-medium mb-12px">{{ question['title'] }}</label>
            <vue-select
                :id="'question_'+question['id']"
                :options="question['options']"
                :title="question['title']"
                :placeholder="`Select ${question['title']}`"
                :color="color"
                :name="'question_'+question['id']"
                :value="customFields['question_'+question['id']] || ''"
                @input="storeSingleSelect($event, 'question_'+question['id'])"
                :multi-select="false"
                :searchable="true"
                v-validate="question['is_required'] && 'required'"
                class="dropdown-select"
            />

            <!--Hack to support v-select as regular html form submission -->
            <input type="hidden" :name="'question_'+question['id']" :value="customFields['question_'+question['id']]" />
          </div>
          <ul class="centerx" v-else-if="question['type'] === 'radio-group'">
            <li v-for="(option, i) in question['options']" :key="i">
                <vs-radio v-model="customFields['question_'+question['id']]" v-validate="question['is_required'] && 'required'" :vs-value="option.code" :name="'question_'+question['id']">{{ option.label }}</vs-radio>
            </li>
          </ul>

          <span class="text-danger text-10" v-show="errors.has('question_'+question['id'])">
            {{ errors.first('question_'+question['id']) }}
          </span>
        </div>

        <div class="mt-6">
          <p class="text-danger text-10 mb-1" v-show="sessionErrors" v-for="sessionError in sessionErrors" :key="sessionError">
            {{ sessionError }}
          </p>
        </div>

        <input type="hidden" name="gpc" :value="gpcValue === true ? 1 : 0">

        <div v-if="hiring_event_toggle">
          <input type="hidden" name="selected_date" v-model="selected_date">
          <input type="hidden" name="selected_time_slot" :value="selectedTimeSlotInfo">
          <input type="hidden" name="selected_positions" :value="selectedPositionsString">
        </div>

        <p v-if="rktTcpa && rktTcpa.content && showTcpaError && !rktTcpaChecked" class="text-danger text-10 mt-5">
           Please check this box to continue
        </p>

        <div v-if="rktTcpa && rktTcpa.content" class="d-flex align-items-start" :class="[showTcpaError && !rktTcpaChecked ? 'mt-1' : 'mt-4']">
          <p v-if="showTcpaError && !rktTcpaChecked" style="position: absolute" class="text-danger text-16 left-16px mt-2px"><i class="fas fa-exclamation"></i></p>
          <input type="checkbox" v-model="rktTcpaChecked" class="mr-2 mt-1" :class="{ 'checkbox-error': showTcpaError && !rktTcpaChecked, 'checkbox-appearance': !showTcpaError && rktTcpaChecked }">
          <p class="text-10 privacy" v-html="rktTcpa.content"></p>
        </div>

        <!-- Submit Button -->
        <primary-button
            size="medium"
            class="w-full mt-6"
            :class="{
              'button-disabled':
                loading
                || (Object.keys(this.rktTcpa || {}).length > 0 && rktTcpa.content && !rktTcpaChecked)
                || errors.has('zip_code')
            }"
            :color="color"
            :bg-color="color"
            @click="handleClick"
            :disabled="loading || errors.has('zip_code')"
            :style="getCustomStyle"
          >
          {{ job.media_page_content.custom_cta }} <span class="ml-2" v-show="loading"><i class="fas fa-circle-notch fa-spin"></i></span>
          </primary-button>

        <div class="d-flex align-items-start mt-4">
          <input v-if="tcpa_opt_in" type="checkbox" v-model="tcpa_opt_in_default" class="mr-2 mt-1">
          <p class="text-10 privacy" v-html="job.tcpa_content"></p>
        </div>

          <p class="text-10 mt-4 privacy" v-if="!job.tcpa_content || job.tcpa_content.length==0">By submitting this form, I authorize CDL Job Now to contact me via phone, SMS, and email regarding employment opportunities, including via automated dialing technology & pre-recorded voice. My consent is not a condition of receiving services. Message and data rates may apply. I expressly agree to this <a target="_blank" :href="`privacy-policy`" class="">Privacy Policy</a> and <a target="_blank" :href="`terms-of-use`" class="">Terms of Use</a>.</p>
      </div>
    </form>
  </div>
</template>

<script>
import { Validator } from 'vee-validate';
import FormatPhoneNumberMixin from "../../mixins/FormatPhoneNumberMixin";
import HandleBackEndErrorsMixin from "../../mixins/HandleBackEndErrorsMixin";
import MediaRadioButton from '../../components/MediaRadioButton.vue';
import event from "../../event";
import GetHiringEventSlotsMixin from '../../mixins/GetHiringEventSlotsMixin';
import PlaceAutoSuggest from "../../../../vendor/careernowbrands/core/resources/js/components/organisms/PlaceAutoSuggest.vue"
import CategoryTypesLoader from "./CategoryTypesLoader.vue";
import Endorsements from "./form-type-input-fields/Endorsements.vue";
import {getFormTypeImplementation, getFormTypeInputFieldsConfig} from "../../form-types/form-type-implementations";
import VueSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

// Register custom US phone validator to match backend logic
Validator.extend('usphone', {
  getMessage: field => 'The mobile phone field format is invalid',
  validate: value => {
    let digits = value.replace(/\D/g, '');

    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }

    if (digits.length !== 10) return false;

    const formatRegex = /^(\(?[0-9]{1,3}\)?)?\s?[0-9]{0,3}-?[0-9]{0,4}$/;
    if (!formatRegex.test(value)) return false;

    // This ensures area code and exchange code start with 2-9 and second digit of area code is not same as first
    return /^([2-9])([0-9])(?!\2)([0-9])([2-9])([0-9]){6}$/.test(digits);
  }
});

// Register the custom validation rule
Validator.extend('differentNames', {
    getMessage: () => 'First and Last Names cannot be the same',
    validate: (value, [otherNameValue]) => {
        if (!value || !otherNameValue) return true;  // Allow empty values (handled separately by "required")
        return value?.trim().toLowerCase().replace(/\s+/g, '') !== otherNameValue?.trim().toLowerCase().replace(/\s+/g, '');
    }
}, { hasTarget: true });

const SESSION_ALLOWED_FIELDS = ['first_name', 'last_name', 'email', 'phone', 'full_address', 'address', 'city', 'state', 'zip_code', 'apartment'];
const SESSION_MEDIA_FORM_FIELDS = ['experience', 'valid_cdl', 'endorsements', 'owner_operator', 'valid_cdl', 'valid_driver_license', 'license_type'];

export default {
  name: "MediaForm",
  components: { MediaRadioButton, CategoryTypesLoader, PlaceAutoSuggest, Endorsements, VueSelect },
  mixins: [
    FormatPhoneNumberMixin,
    HandleBackEndErrorsMixin,
    GetHiringEventSlotsMixin
  ],
  props: {
    overrideBgColor: {
        type: String,
        default: null,
    },
    company: {
      type: Object,
    },
    job: {
      type: Object,
    },
    application : {
      type : Boolean,
      default : false,
    },
    redirectPage : {
      type : String,
      default : '',
    },
    sessionErrors: {
      type: Array,
      default : () => { return [] }
    },
    formQuestionLabels: {
      required: true,
      type: Array | Object,
    },
    rktTcpa: {
      type: Array | Object,
      default: () => { return {} }
    }
  },

  mounted() {
    this.showTcpaError = false;
    this.rktTcpaChecked = false;
    // Apply watchers after all other initialization is done;
    if (window.errors) {
      this.sessionErrors = window.errors;
    }
    this.applyFullFormWatchers();
    this.loading = false;
    this.positions = this.job['job_position_data'];
    // Set GPC value based on browser's Global Privacy Control signal
    this.gpcValue = navigator.globalPrivacyControl;

    this.formHeight();
  },

  data() {
    return {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      full_address: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      apartment: '',
      hiring_event_slots: this.job.hiring_event_slots,
      selected_date: null,
      selected_time_slot: null,
      selected_positions: null,
      timeSlotOptionsData: [],
      mediaFullForm: {
        experience: '',
        endorsements: [],
      },
      customFields: {},
      gpcValue: false,
      multiSelects : {},
      loading: false,

      // We store the dynamic category selection in here
      categorySelection: {},

      // Set the initial state of the checkbox based on `job.tcpa_opt_in`
      tcpa_opt_in_default: false,
      tcpa_opt_in: this.job.tcpa_opt_in,

      experiences: this.job.formTypeInputFieldOptions.experiences,
      endorsementsOptions: this.job.formTypeInputFieldOptions.endorsements,
      questions: this.job.questions,

      // Receive and store Form Type specific options
      formTypeInputFieldOptions: this.job.formTypeInputFieldOptions,

      positions: [],
      rktTcpaChecked: false,
      showTcpaError: false,
      storeLocations: [],
      fetchingLocations: false,
      store_location: {},
      latitude: '',
      longitude: '',
    }
  },

  watch: {
    zip_code: {
      handler() {
        this.validateZipCodeState();
        this.getStoreLocations();
      },
      deep: true
    },
    state: {
      handler() {
        this.validateZipCodeState();
      },
      deep: true
    },
    formData: {
      handler() {
        sessionStorage.setItem('mediaFormData', JSON.stringify(this.formData));
      },
      deep: true
    }
  },

  created(){
    this.rktTcpaChecked = false;
    this.fetchPositions();

    this.initializeDictionary();

    // REFACTOR NOTE: Previously, data() properties were being overridden from sessionStorage without filtering,
    // which caused issues with field options not loading correctly based on job data.
    // Now we only restore specific allowed fields from sessionStorage to prevent these conflicts.
    const mediaFormData = JSON.parse(sessionStorage.getItem('mediaFormData'));
    if (mediaFormData) {
      Object.keys(mediaFormData).forEach(key => {
        if (SESSION_ALLOWED_FIELDS.includes(key)) {
          this[key] = mediaFormData[key];
        }
        if (SESSION_MEDIA_FORM_FIELDS.includes(key)){
            this.mediaFullForm[key] = mediaFormData[key];
        }
      });
    }

    this.selected_positions = null;
    this.selected_date = null;
    this.selected_time_slot = null;
    if(this.groupedSlots.length == 1) {
      this.selected_date = this.groupedSlots[0].date;
      this.timeSlotOptionsData = this.timeSlotOptions(this.filteredSlots(this.selected_date));
    }
  },

  computed: {
      timezoneMapping() {
        return this.job.timezone_mapping || '';
      },

      hiring_event_toggle() {
        return this.job.is_hiring_event;
      },
      selectedTimeSlotInfo() {
        if (!this.selected_time_slot) {
          return null;
        }
        const selectedSlot = this.timeSlotOptionsData.find(slot => slot.value === this.selected_time_slot);
        return selectedSlot ? selectedSlot.label : null;
      },
      getCustomStyle() {
          if (this.overrideBgColor) {
              return `background-color: ${this.overrideBgColor}`;
          }

          return '';
      },
      getBgColor() {
          if (this.overrideBgColor) {
              return this.overrideBgColor;
          }

          return this.job['color'] ? this.job['color'] : (this.company ? this.company['color'] : '#002f65');
      },

    formData() {
      const data = { ...this.$data, ...this.$data.mediaFullForm };

      // Use the gpcValue that was already set in mounted(), don't read navigator.globalPrivacyControl again
      data.gpcValue = this.gpcValue;

      // Clean up internal tracking properties before storing in sessionStorage
      // These properties should not persist between sessions as they're regenerated
      // when the component is created or would conflict with server data
      delete data.customFields;
      delete data.dirtyFields;
      delete data.multiSelects;
      delete data.endorsementsOptions;
      delete data.experiences; // Experiences must be freshly loaded from job data each time
      return data;
    },
    color() {
      return this.job['color'] ? this.job['color'] : (this.company ? this.company['color'] : '#002f65');
    },
    colorCleaned() {
      return this.company ? this.company['color_cleaned'] : '#002f65';
    },
    groupedSlots() {
      const slots = this.getHiringEventSlots(this.job.hiring_event_slots || []);
      const groups = slots.reduce((acc, slot) => {
        const date = slot.date;
        if (!acc[date]) {
          acc[date] = {
            date,
            slots: []
          };
        }
        acc[date].slots.push({
          id: slot.id,
          label: `${slot.originalStartTime12Hour} ${slot.originalEndTime12Hour ? ' - ' + slot.originalEndTime12Hour : ''}`,
          limit: slot.original.limit,
          full: slot.full // Include full status if needed
        });
        return acc;
      }, {});
      // Filter out dates where all slots are full
      const filteredGroups = Object.values(groups).filter(group =>
        group.slots.some(slot => !slot.full)
      );

      return filteredGroups;
    },
    multiplePositions() {
      return this.positions.length > 1;
    },

    dateOptions() {
      return this.groupedSlots.map(group => ({
        value: group.date,
        label: group.date
      }));
    },

    selectedPositionsString() {
      return Array.isArray(this.selected_positions)
        ? this.selected_positions.map(position => position.label).join(',')
        : '';
    },

    isExperienceFieldDisabled() {
        return getFormTypeImplementation(this.job.form_type.type)
            .mediaFormIsExperienceDisabled(this.mediaFullForm, this)
    },

    formTypeInputFields() {
      const allOptionsData = this.formTypeInputFieldOptions
      return getFormTypeInputFieldsConfig(this.job.form_type.type, allOptionsData);
    },
  },

  methods: {

    async getStoreLocations() {
      this.fetchingLocations = true;
      this.store_location = {}; // reset store_location

      let { data } = await axios.get(
        `/api/job/${this.job.id}/job-locations`,
        {
          params: {
            zipCode: this.zip_code,
            latitude: this.latitude,
            longitude: this.longitude,
          }
        }
      );

      this.fetchingLocations = false;
      // Process the new data structure
      this.storeLocations = data.map(location => ({
        ...location,
        value: location.value,
        label: `${location.address} ${location.city}, ${location.state} - ${location.cityDistance}`
      }))
    },

    triggerValidation(fieldName) {
        if (this.first_name && this.last_name) {
            this.$validator.validate(fieldName);
        }
    },

    validateIfCustomExperiencesExistsInOptions() {
      if (this.mediaFullForm.experience != '') {
        const isValid = this.experiences.some(
          experience => experience.value === this.mediaFullForm.experience
        );

        if (!isValid) {
          this.mediaFullForm.experience = '';
        }
      }
    },

    setCategoryValue(type, value) {
      this.categorySelection = {
        ...this.categorySelection,
        [type]: value,
      };
    },

    /**
     * Disable the submit button before any validation requests run so a slow
     * network cannot be used to submit the form more than once.
     */
    handleClick(event) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.disableSubmitButton(event);
      this.submitForm();
    },

    disableSubmitButton(event) {
      const target = event && (event.currentTarget || event.target);
      const button = target && target.closest ? target.closest('button') : null;

      if (button) {
        button.disabled = true;
      }
    },

    filteredSlots(date) {
      const group = this.groupedSlots.find(group => group.date === date);
      return group ? group.slots.filter(slot => !slot.full) : [];
    },
    onDateChange() {
      this.timeSlotOptionsData = this.timeSlotOptions(this.filteredSlots(this.selected_date));
      this.selected_time_slot = null; // Reset selected time slot when date changes
      this.formHeight();
    },
    timeSlotOptions(slots) {
      return slots.map(slot => ({
        value: slot.id,
        label: slot.label
      }));
    },

    async initializeDictionary() {
      let dict = {
        custom: {
          first_name: {
              required: 'First Name field is required',
              alpha_spaces: 'Your First Name may only contain alphabetic characters',
              differentNames: 'First and Last Names cannot be the same',
          },
          last_name: {
              required: 'Last Name field is required',
              alpha_spaces: 'Your Last Name may only contain alphabetic characters',
              differentNames: 'First and Last Names cannot be the same',
          },
          email: {
              required: 'Email field is required',
              email: 'Enter a valid email address'
          },
          phone: {
              required: 'Mobile Phone field is required',
              regex: 'The mobile phone field format is invalid'
          },
          address: {
              required: 'Address field is required',
          },
          full_address: {
              required: 'Address field is required',
          },
          city: {
              required: 'City field is required',
          },
          state: {
              required: 'State field is required',
          },
          zip_code: {
              required: 'Zip Code field is required',
          },
          experience: {
            required: 'Experience field is required',
          },
          selected_date:  {
              required: 'Date field is required',
          },
          selected_time_slot:  {
              required: 'Time slot field is required',
          },
          selected_positions:  {
              required: 'Position field is required',
          },
          endorsements:  {
              required: 'Endorsements field is required',
          },
          store_location: {
            required: 'Store Location field is required',
          },
        }
        };

      this.validateIfCustomExperiencesExistsInOptions();

      this.questions.forEach(question => {
          this.customFields[`question_` + question.id] = '';

          dict.custom[`question_` + question.id] = {
              required: question.title + ' field is required'
          };
      });

      await this.formHeight();

      // register custom messages
      Validator.localize('en', dict);
    },

    fetchPositions() {
      const fetchedPositions = this.job.job_position_data || [];
      this.positions = fetchedPositions;
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    async formHeight() {
      await this.$nextTick();

      let height = this.$refs.mediaFormWrapper.clientHeight;
      event.bus.$emit('media_form_height', height);
    },
    applyFullFormWatchers() {

      this.$watch("mediaFullForm", (newMediaFullForm) => {
        getFormTypeImplementation(this.job.form_type.type)
            .mediaFormUpdated(newMediaFullForm, this)
      }, {deep: true})

    },

    async validateTimeSlotAvailiblity() {
      if(this.selected_time_slot) {
        const response = await axios.post(
          `/jobs/${this.job.id}/rsvp/${this.selected_time_slot}`,
        );
        if (!response.data.success) {
          this.selected_time_slot = null;
          this.errors.add({ field: 'selected_time_slot', msg: response.data.message });
          return false;
        }
      }
      return true;
    },

    async validateZipCodeState() {
      // Validation:ensure lies within the provided state
      if(this.zip_code && this.state) {
          let { data } = await axios.get(
            '/api/confirm-state-zip-code',
            { params: {
              zipCode: this.zip_code,
              state: this.state
            } }
          );

          if(!data.zip_exists_in_state) {
            this.errors.add({
              field: 'zip_code',
              msg: 'Please enter a valid zip code within the state'
            });
            return false;
          }else {
            // Clear any existing validation error for the zip_code field
            if (this.errors.has('zip_code')) {
              this.errors.remove('zip_code');
            }
            return true;
          }
        }
    },
    storeSingleSelect(event, question) {
      this.customFields[question] = event.label;
    },
    storeMultiSelect(event, question){
      this.multiSelects[question] = event;
      let answers = '';
      event.forEach(answer => {
        if (!answers) {
          answers = answer.label;
        } else {
          answers += ' & '+ answer.label;
        }
      })
      this.customFields[question] = answers;
    },
    getValuesFromOptions(option) {
      return option.value;
    },
    autoFillAddressFields(value) {
      this.state = value?.administrative_area_level_1 ?? '';
      this.city = value?.locality ?? '';
      this.zip_code = value?.postal_code ?? '';
      this.address = value?.name ?? '';
      this.full_address = value?.full ?? '';
      this.formHeight(); // recalculate form height when address fields show
    },
    noAddressResultsFound() {
      if(this.errors.has('full_address')) this.errors.remove('full_address');

      this.errors.add({ field: 'full_address', msg: 'Enter a valid address' });
    },
    async submitForm() {
      if (!this.loading) {
        this.loading = true;
      }

      let submitted = false;

      try {
        const result = await this.$validator.validateAll();
        const timeSlotCheck = await this.validateTimeSlotAvailiblity();
        const zipStateValid = await this.validateZipCodeState();

        if (timeSlotCheck && result && this.isPhoneNumberValid(this.phone) && zipStateValid) {
          sessionStorage.setItem('mediaFormData', JSON.stringify(this.formData));
          this.$refs.mediaForm.submit();
          submitted = true;
        }
      } finally {
        if (!submitted) {
          this.loading = false;
        }
      }
    },
    formatPhoneNumber(ev) {
      this.phone = this.setFormatPhoneNumber(ev, this.phone);
    },
    isPhoneNumberValid(input) {
        var validatePhoneNumberRegex = /^\([0-9]{3}\)\s[0-9]{3}-[0-9]{4}$/;
        if (!(validatePhoneNumberRegex.test(input))) {
            this.errors.add({ field: 'phone', msg: 'The mobile phone field format is invalid' });
            return false;
        }
        return true;
    },
    validatePhoneNumberOnBlur(e) {
      this.formatPhoneNumber(e);
      this.isPhoneNumberValid(e.target.value);
    }
  }
}
</script>

<style lang="scss">
  .media-form {
    .privacy {
      a {
        color: blue !important;
        text-decoration: underline !important;
      }
    }

    select {

      font-family: "Montserrat", sans-serif !important;
      font-size: 14px !important;
      color: #202028 !important;

      &::placeholder,
      &::-moz-placeholder,
      &::-webkit-input-placeholder,
      &:-ms-input-placeholder {
        font-family: "Montserrat", sans-serif !important;
        font-size: 14px !important;
        color: #202028 !important;
      }
    }

    .vs-con-input input {
      font-family: "Montserrat", sans-serif !important;
      color: #202028 !important;
    }

    .v-select {
      .vs__selected{
        background: var(--company-color) !important;
        color: white !important;
        white-space: normal !important;
        overflow: visible !important;
        text-overflow: unset !important;
        word-break: break-word;
      }
    }

    .vs__dropdown-menu  {
      .vs__dropdown-option.vs__dropdown-option--highlight {
        background: var(--company-color) !important;
        color: white !important;
      }
    }

  }

  .d-flex {
    display: flex;
  }
  .align-items-start {
    align-items: flex-start;
  }
  .mt-1 {
    margin-top: 0.25rem;
  }
  .mr-2 {
    margin-right: 0.5rem;
  }
  .time_slots {
    .option {
      padding-right: 9px !important;
      padding-left: 9px !important;
    }
  }

  /* Highlight error state */
  input[type="checkbox"].checkbox-error {
    border: none;
    appearance: none;
    width: 40px;
    height: 13px;
    border: 2px solid #4dcc4d; /* Add green border */
  }

  input[type="checkbox"].checkbox-appearance {
    appearance: auto;
  }

  .text-danger {
    color: #dc3545;
  }

  .button-disabled {
    background: #b1b1b3 !important;
    color: #ffffff;
  }

  .vs__selected-options,
  .vs__dropdown-menu li {
    overflow: visible;
    text-overflow: unset;
    white-space: normal;
    word-break: break-word;
  }

  .v-select.dropdown-select .vs__dropdown-toggle {
    height: 48px !important;
  }

  .v-select.dropdown-select .vs__selected {
    padding: 0 8px !important;
    line-height: 1.2;
  }
</style>
